import { Accessor, createMemo } from "solid-js";
import {
  Channel,
  Client,
  Message,
  Server,
  ServerMember,
  ServerRole,
  User,
} from "stoat.js";

export interface AutoCompleteSearchSpace {
  users?: User[];
  members?: ServerMember[];
  channels?: Channel[];
  roles?: ServerRole[];
  canMentionEveryone?: boolean;
  canMentionRoles?: boolean;
}

function generateSearchSpaceFrom(
  object: Client | Server | Channel | Message,
  client: Client,
): AutoCompleteSearchSpace {
  if (object instanceof Message) {
    if (object.channel) return generateSearchSpaceFrom(object.channel, client);
  } else if (object instanceof Channel) {
    const mentionPermissions = {
      canMentionEveryone: object.havePermission("MentionEveryone"),
      canMentionRoles: object.havePermission("MentionRoles"),
    };

    if (object.server)
      return {
        ...generateSearchSpaceFrom(object.server, client),
        ...mentionPermissions,
      };

    if (object.type === "Group" || object.type === "DirectMessage") {
      return {
        users: object.recipients,
        ...mentionPermissions,
      };
    }
  } else if (object instanceof Server) {
    return {
      members: client.serverMembers.filter(
        (member) => member.id.server === object.id,
      ),
      channels: object.channels,
      roles: [...object.roles.values()],
    };
  }

  return {};
}

export function useSearchSpace(
  object: Accessor<Client | Server | Channel | Message>,
  client: Accessor<Client>,
): Accessor<AutoCompleteSearchSpace> {
  const memoised = createMemo(() =>
    generateSearchSpaceFrom(object(), client()),
  );

  return memoised;
}
